import React, { useMemo } from 'react';
import { Modal, StyleSheet, Text, View } from 'react-native';
import { AppButton } from '../../components/AppButton';
import { AppScreen } from '../../components/AppScreen';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useNetworkConnectivity } from '../../context/NetworkConnectivityContext';
import { useAppColors } from '../../context/PreferencesContext';

function OfflineIcon({
  color,
  backgroundColor,
  borderColor,
}: {
  color: string;
  backgroundColor: string;
  borderColor: string;
}) {
  return (
    <View
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
      style={[styles.iconCircle, { backgroundColor, borderColor }]}
    >
      <View style={[styles.arcLarge, { borderColor: color }]} />
      <View style={[styles.arcMid, { borderColor: color }]} />
      <View style={[styles.dot, { backgroundColor: color }]} />
      <View style={[styles.slash, { backgroundColor: color }]} />
    </View>
  );
}

export function NoInternetConnectionScreen() {
  const palette = useAppColors();
  const { isChecking, retry } = useNetworkConnectivity();

  const themed = useMemo(
    () => ({
      title: {
        ...typography.screenTitle,
        color: palette.textPrimary,
        textAlign: 'center' as const,
      },
      message: {
        ...typography.body,
        color: palette.textSecondary,
        textAlign: 'center' as const,
      },
    }),
    [palette],
  );

  return (
    <AppScreen contentStyle={styles.screen} webLayout="form">
      <View
        style={styles.content}
        accessibilityRole="alert"
        accessibilityLabel="No internet connection. Check your Wi-Fi or mobile data and try again."
        accessibilityLiveRegion="assertive"
      >
        <OfflineIcon
          color={palette.error}
          backgroundColor={palette.surface}
          borderColor={palette.border}
        />
        <Text style={themed.title}>No Internet Connection</Text>
        <Text style={themed.message}>
          Check your Wi-Fi or mobile data and try again.
        </Text>
        <AppButton
          title="Try again"
          onPress={() => {
            retry().catch(() => undefined);
          }}
          loading={isChecking}
          accessibilityLabel="Try Again"
          style={styles.retry}
        />
      </View>
    </AppScreen>
  );
}

export function NoInternetConnectionGate() {
  const { isOffline } = useNetworkConnectivity();
  if (!isOffline) {
    return null;
  }

  return (
    <Modal
      visible
      animationType="fade"
      presentationStyle="fullScreen"
      statusBarTranslucent
      onRequestClose={() => undefined}
    >
      <NoInternetConnectionScreen />
    </Modal>
  );
}

const styles = StyleSheet.create({
  screen: {
    justifyContent: 'center',
  },
  content: {
    alignItems: 'center',
    gap: spacing.lg,
    paddingHorizontal: spacing.md,
  },
  retry: {
    alignSelf: 'stretch',
    marginTop: spacing.sm,
  },
  iconCircle: {
    width: 96,
    height: 96,
    borderRadius: 48,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
    borderWidth: 1,
  },
  arcLarge: {
    position: 'absolute',
    width: 44,
    height: 44,
    borderRadius: 22,
    borderWidth: 3,
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    transform: [{ rotate: '-45deg' }, { translateY: -8 }],
  },
  arcMid: {
    position: 'absolute',
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 3,
    borderBottomColor: 'transparent',
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
    transform: [{ rotate: '-45deg' }, { translateY: -2 }],
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    marginTop: 18,
  },
  slash: {
    position: 'absolute',
    width: 4,
    height: 52,
    borderRadius: 2,
    transform: [{ rotate: '28deg' }],
  },
});
